import { Component, ElementRef, Input, AfterViewInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import cytoscape, { Core, ElementDefinition } from 'cytoscape';

@Component({
  selector: 'app-graph',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="graph-root">
      <div class="graph-toolbar">
        <div class="graph-toolbar-title">         
          <div class="graph-actions">
            <button type="button" class="toolbar-btn" (click)="fitGraph()" aria-label="Fit graph">Fit</button>
            <button type="button" class="toolbar-btn" (click)="zoomIn()" aria-label="Zoom in">+</button>
            <button type="button" class="toolbar-btn" (click)="zoomOut()" aria-label="Zoom out">−</button>
            <button type="button" class="toolbar-btn" (click)="resetGraph()" aria-label="Reset view">Reset</button>
          </div>
        </div>

        <div class="graph-legend">
          <div class="legend-item source">
            <span class="legend-key"></span>
            Source nodes
          </div>
          <div class="legend-item intermediate">
            <span class="legend-key"></span>
            Intermediate nodes
          </div>
          <div class="legend-item target">
            <span class="legend-key"></span>
            Target nodes
          </div>
        </div>
      </div>

      <div #graphContainer class="graph-container"></div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
    }

    .graph-root {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      min-height: 0;
    }

    .graph-toolbar {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 0 10px;
    }

    .graph-toolbar-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 6px;
    }

    .graph-title {
      color: #0f172a;
      font-size: 1rem;
      font-weight: 700;
    }

    .graph-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .toolbar-btn {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
      border-radius: 10px;
      padding: 8px 12px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.18s ease;
    }

    .toolbar-btn:hover {
      border-color: #65a7ff;
      color: #6D28D9;
      background: #f8fbff;
    }

    .graph-legend {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.75rem;
    }

    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: #475569;
      font-size: 12px;
      font-weight: 500;
    }

    .legend-key {
      width: 12px;
      height: 12px;
      border-radius: 999px;
      display: inline-flex;
      border: 1px solid transparent;
    }

    .legend-item.source .legend-key {
      background: #EDE9FE;
      border-color: #C4B5FD;
    }

    .legend-item.intermediate .legend-key {
      background: #f8fafc;
      border-color: #cbd5e1;
    }

    .legend-item.target .legend-key {
      background: #dcfce7;
      border-color: #86efac;
    }

    .graph-container {
      position: relative;
      width: 100%;
      flex: 1;
      min-height: 0;
      border-radius: 24px;
      background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);
      overflow: hidden;
    }
  `]
})
export class GraphComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('graphContainer') graphContainer!: ElementRef<HTMLDivElement>;
  @Input() elements: ElementDefinition[] = [];

  private cy: Core | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimer: any = null;
  private readonly overviewPadding = 90;
  private readonly overviewZoomFactor = 0.9;
  private readonly topViewportMargin = 28;

  ngAfterViewInit(): void {
    this.initGraph();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['elements'] && !changes['elements'].firstChange && this.cy) {
      this.updateGraph();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.cy) {
      this.cy.destroy();
    }
  }

  zoomIn(): void {
    if (!this.cy) return;
    const nextZoom = Math.min(this.cy.zoom() * 1.2, 3);
    this.cy.zoom({
      level: nextZoom,
      position: { x: this.cy.width() / 2, y: this.cy.height() / 2 }
    });
  }

  zoomOut(): void {
    if (!this.cy) return;
    const nextZoom = Math.max(this.cy.zoom() * 0.8, 0.35);
    this.cy.zoom({
      level: nextZoom,
      position: { x: this.cy.width() / 2, y: this.cy.height() / 2 }
    });
  }

  fitGraph(): void {
    if (!this.cy) return;
    setTimeout(() => {
      if (this.cy) {
        this.cy.resize();
        this.cy.fit(this.cy.nodes(), this.overviewPadding);
      }
    }, 100);
  }

  resetGraph(): void {
    if (!this.cy) return;
    this.applyOverviewViewport();
    this.cy.elements().unselect();
    this.resetEdgeStyles();
  }

  private initGraph(): void {
    const container = this.graphContainer.nativeElement;

    this.cy = cytoscape({
      container,
      elements: this.elements,
      style: [
        {
          selector: 'node',
          style: this.getNodeStyle()
        },
        {
          selector: 'node[nodeType = "source"]',
          style: {
            'background-color': '#eff6ff',
            'border-color': '#C4B5FD',
            'border-width': 2,
            'color': '#6D28D9'
          } as any
        },
        {
          selector: 'node[nodeType = "target"]',
          style: {
            'background-color': '#ecfdf5',
            'border-color': '#86efac',
            'border-width': 2,
            'color': '#166534'
          } as any
        },
        {
          selector: 'node[nodeType = "intermediate"]',
          style: {
            'background-color': '#f8fafc',
            'border-color': '#cbd5e1',
            'border-width': 1.5,
            'color': '#0f172a'
          } as any
        },
        {
          selector: 'node:selected',
          style: {
            'background-color': '#e0ebff',
            'border-color': '#8B5CF6',
            'border-width': 2,
            'shadow-blur': 18,
            'shadow-color': 'rgba(139, 92, 246, 0.18)',
            'shadow-opacity': 1
          } as any
        },
        {
          selector: 'edge',
          style: this.getEdgeStyle()
        },
        {
          selector: 'edge:selected',
          style: {
            'line-color': '#8B5CF6',
            'target-arrow-color': '#8B5CF6',
            'width': 3
          } as any
        }
      ],
      layout: {
        name: 'cose',
        animate: true,
        animationDuration: 800,
        fit: true,
        padding: 60,
        nodeOverlap: 20,
        
        // Massive repulsion force to actively push nodes apart:
        nodeRepulsion: (node: any) => 350000 + (node.degree() * 20000),
        
        // Lower ideal edge length reduces the spring pull factor when nodes are close:
        idealEdgeLength: (edge: any) => 120 + (edge.parallelEdges().length * 15),
        
        // Increasing the elasticity divisor drastically reduces edge tension, allowing repulsion to win:
        edgeElasticity: (edge: any) => 250 * (edge.parallelEdges().length || 1),
        
        gravity: 0.05,                    // Low gravity lets nodes drift safely into the outer canvas
        nestingFactor: 1.2,
        componentSpacing: 250,            
        
        // Slow-cooling variables prevent the layout from freezing before nodes can separate:
        numIter: 1500,                    // Allows plenty of time to find an optimal spread
        initialTemp: 1200,                // Higher starting velocity pushes elements out of overlaps
        coolingFactor: 0.99,              // Cooling down very slowly prevents premature freezing
        minTemp: 1.0,                     
        
        nodeDimensionsIncludeLabels: true,
        randomize: true
      } as any,
      wheelSensitivity: 0.35,
      minZoom: 0.1,
      maxZoom: 3,
      boxSelectionEnabled: true,
      selectionType: 'single'
    });

    this.cy.on('select', 'node', (event) => {
      this.highlightConnectedEdges(event.target);
    });

    this.cy.on('unselect', 'node', () => {
      this.resetEdgeStyles();
    });

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.cy) {
          this.cy.resize();
          this.applyOverviewViewport();
        }
      }, 150);
    });
    this.resizeObserver.observe(container);

    if (this.cy) {
      this.cy.one('layoutstop', () => {
        this.applyOverviewViewport();
      });
    }
  }

  private applyOverviewViewport(): void {
    if (!this.cy) return;

    const nodes = this.cy.nodes();
    if (!nodes.length) return;

    this.cy.resize();
    this.cy.fit(nodes, this.overviewPadding);

    const fitZoom = this.cy.zoom();
    const overviewZoom = Math.max(
      this.cy.minZoom(),
      Math.min(fitZoom * this.overviewZoomFactor, this.cy.maxZoom())
    );

    this.cy.zoom({
      level: overviewZoom,
      renderedPosition: {
        x: this.cy.width() / 2,
        y: this.cy.height() / 2
      }
    } as any);
    this.cy.center(nodes);

    // Ensure the highest node starts slightly below the top edge on initial render.
    const renderedBounds = nodes.renderedBoundingBox();
    const panDeltaY = this.topViewportMargin - renderedBounds.y1;
    this.cy.panBy({ x: 0, y: panDeltaY });
  }

  private getNodeStyle(): any {
    return {
      'label': 'data(label)',
      'background-color': '#ffffff',
      'color': '#0f172a',
      'font-family': 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      'font-size': '11px',
      'font-weight': 600,
      'text-valign': 'center',
      'text-halign': 'center',
      'text-wrap': 'wrap',
      'text-max-width': 150,
      'padding': '12px',
      'border-width': 1.5,
      'border-color': '#cbd5e1',
      'border-opacity': 1,
      'border-style': 'solid',
      'shadow-blur': 10,
      'shadow-color': 'rgba(15, 23, 42, 0.08)',
      'shadow-opacity': 0.25,
      'shadow-offset-x': 0,
      'shadow-offset-y': 2,
      'transition-property': 'border-color, shadow-blur, shadow-opacity, background-color',
      'transition-duration': 200,
      'shape': 'round-rectangle',
      'width': 180,
      'height': 64,
      'line-height': 1.3
    };
  }

  private getEdgeStyle(): any {
    return {
      'width': 1.8,
      'line-color': '#cbd5e1',
      'target-arrow-color': '#64748b',
      'target-arrow-shape': 'triangle',
      'arrow-scale': 1.1,
      'curve-style': 'bezier',
      'line-cap': 'round',
      'line-join': 'round',
      'label': 'data(label)',
      'font-size': 9,                     
      'color': '#475569',
      'font-family': 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      'font-weight': 500,
      'text-background-color': '#ffffff',
      'text-background-opacity': 0.95,
      'text-background-padding': 3,       
      'text-border-color': '#f8fafc',
      'text-border-width': 1,
      'text-border-opacity': 1,
      'text-wrap': 'wrap',                
      'text-max-width': 110,              
      'text-rotation': 'autorotate',
      'edge-text-rotation': 'autorotate'
    };
  }

  private updateGraph(): void {
    if (!this.cy) return;

    this.cy.startBatch();
    this.cy.elements().remove();
    this.cy.add(this.elements);
    this.cy.endBatch();

    const layout = this.cy.layout({
      name: 'cose',
      animate: true,
      animationDuration: 800,
      fit: true,
      padding: 60,
      nodeOverlap: 20,
      nodeRepulsion: (node: any) => 350000 + (node.degree() * 20000),
      idealEdgeLength: (edge: any) => 120 + (edge.parallelEdges().length * 15),
      edgeElasticity: (edge: any) => 250 * (edge.parallelEdges().length || 1),
      gravity: 0.05,
      nestingFactor: 1.2,
      componentSpacing: 250,
      numIter: 1500,
      initialTemp: 1200,
      coolingFactor: 0.99,
      minTemp: 1.0,
      nodeDimensionsIncludeLabels: true,
      randomize: true
    } as any);

    layout.on('layoutstop', () => {
      this.applyOverviewViewport();
    });
    layout.run();
  }

  private highlightConnectedEdges(node: any): void {
    this.cy?.edges().style({
      'opacity': 0.2,
      'width': 1
    } as any);

    const connectedEdges = node.connectedEdges();
    connectedEdges.style({
      'opacity': 1,
      'width': 3,
      'line-color': '#8B5CF6',
      'target-arrow-color': '#8B5CF6'
    } as any);
  }

  private resetEdgeStyles(): void {
    this.cy?.edges().style({
      'opacity': 1,
      'width': 1.8,
      'line-color': '#cbd5e1',
      'target-arrow-color': '#64748b'
    } as any);
  }
}